import { useEffect, useRef } from "react";
import { TakeOffSourceType, useDatasources } from "../context/DatasourceContext";
import { useDrawerActions } from "../context/DrawerContext";

/** The connection lifecycle every take-off source shares. */
export type TakeOffConnectionState = "login" | "selector" | "loading" | "connected";

/**
 * Keeps a take-off list in step with its source's connection, firing on transitions rather than
 * on the state itself — the ref is seeded from the mount-time value, so opening the drawer to
 * edit an already connected source sees no transition and does not close it.
 *
 * On connect: records the source and closes the drawer. This covers the cached-session login
 * path, where `initialize<Source>State` jumps straight from login to connected.
 *
 * On disconnect: calls `onDisconnect` so the list can drop its local selection, covering a
 * disconnect that lands while the list is still mounted. Note `FullScreenDrawerLayout` does not
 * set `keepMounted`, so a closed drawer unmounts its content once the exit transition finishes —
 * but not before, which is why the reset cannot be left to unmounting.
 *
 * `loading` is not a disconnect. Re-selecting on an already connected source runs
 * connected → loading → connected, and treating the middle step as a disconnect would clear the
 * list's selection out from under the request still using it.
 */
export const useConnectionTransition = (
  sourceId: TakeOffSourceType,
  state: TakeOffConnectionState,
  onDisconnect?: () => void
) => {
  const { addDatasource } = useDatasources();
  const { closeDrawer } = useDrawerActions();
  const isConnected = state === "connected";
  const wasConnected = useRef<boolean>(isConnected);
  const onDisconnectRef = useRef(onDisconnect);
  onDisconnectRef.current = onDisconnect;

  useEffect(() => {
    if (isConnected && !wasConnected.current) {
      addDatasource("takeOff", sourceId);
      closeDrawer();
    } else if (!isConnected && wasConnected.current && state !== "loading") {
      onDisconnectRef.current?.();
    }
    wasConnected.current = isConnected;
  }, [isConnected, state, sourceId, addDatasource, closeDrawer]);
};

export default useConnectionTransition;
